export type IconName =
  | 'search'
  | 'shield'
  | 'deck'
  | 'people'
  | 'chat'
  | 'agenda'
  | 'table'
  | 'check'
  | 'download'
  | 'revert'
  | 'eye'
  | 'eyeOff'
  | 'close'
  | 'info';

const PATHS: Record<IconName, string> = {
  search: '<circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.5" y2="16.5"/>',
  shield: '<path d="M12 3l7 3v6c0 5-3.5 8-7 9-3.5-1-7-4-7-9V6l7-3z"/>',
  deck: '<rect x="3" y="4" width="18" height="13" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>',
  people:
    '<circle cx="9" cy="8" r="3"/><path d="M2 20c0-3.5 3-6 7-6s7 2.5 7 6"/><circle cx="17" cy="9" r="2.5"/><path d="M15.5 14.2c2.7.4 4.5 2.5 4.5 5.8"/>',
  chat: '<path d="M4 4h16v11H8l-4 4V4z"/>',
  agenda:
    '<line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="18" x2="14" y2="18"/>',
  table:
    '<rect x="3" y="4" width="18" height="16" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="9" y1="10" x2="9" y2="20"/>',
  check: '<path d="M9 11h11"/><path d="M9 11l-4 4L2 12"/>',
  download: '<path d="M12 3v12"/><path d="M7 11l5 5 5-5"/><path d="M4 20h16"/>',
  revert: '<path d="M4 10h9a5 5 0 1 1 0 10H8"/><path d="M8 6l-4 4 4 4"/>',
  eye: '<path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z"/><circle cx="12" cy="12" r="3"/>',
  eyeOff:
    '<path d="M2 12s3.5-6 10-6c1.6 0 3 .35 4.2.9"/><path d="M21.1 8.6c.6.8.9 1.4.9 1.4s-3.5 6-10 6c-1 0-1.9-.14-2.7-.37"/><line x1="3" y1="3" x2="21" y2="21"/>',
  close: '<line x1="5" y1="5" x2="19" y2="19"/><line x1="19" y1="5" x2="5" y2="19"/>',
  info: '<circle cx="12" cy="12" r="9"/><line x1="12" y1="11" x2="12" y2="16.5"/><circle cx="12" cy="7.5" r="0.5" fill="currentColor"/>',
};

/**
 * Coerce an authored icon value to a known {@link IconName}, falling back to `fallback` when it is
 * empty, whitespace-only, or not one of the icons this set can draw. Authored icon names come from a
 * free-text admin field, so a stray trailing space or an unknown name would otherwise reach `Icon` and
 * render an empty `<svg>` (`PATHS[name]` is `undefined`) — this keeps the primitive's default showing.
 */
export function asIconName(value: unknown, fallback: IconName): IconName {
  if (typeof value === 'string') {
    const key = value.trim();
    if (key in PATHS) return key as IconName;
  }
  return fallback;
}

interface IconProps {
  name: IconName;
  size?: number;
  strokeWidth?: number;
}

export function Icon({ name, size = 14, strokeWidth = 2 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      dangerouslySetInnerHTML={{ __html: PATHS[name] }}
    />
  );
}

export function CheckBadge({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="#2e7d32" aria-hidden="true">
      <circle cx="12" cy="12" r="11" />
      <path
        d="M7 12.5l3.2 3.2L17 9"
        stroke="white"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
